import {DrivingInfoTripProps} from '@di-shared/features/history/driving-info/DrivingInfoTrip/index';
import {ErrorBoundary} from 'react-error-boundary';
import {Suspense, useState} from 'react';
import {useNavigate} from 'react-router';
import {getDateFnsLocale, useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import useTripDayKr, {getWeekIndexOfDate, getWeeksOfMonth, isTripOnDay} from '@di-shared/features/history/date/useTripDay.kr';
import ReactDatePicker from 'react-datepicker';
import {Pagination} from 'swiper/modules';
import {Swiper, SwiperSlide} from 'swiper/react';
import {getYYYYMMddFormat, getYYYYMMFormat} from '@di-shared/api/di/utils';
import {isSameYearAndMonth} from '@shared/dateUtils';
import {emitEventLog} from '@di-shared/features/analytics';
import {ROUTE_PATHS} from '@di-shared/routes';
import DrivingInfoTripContent from '@di-shared/features/history/driving-info/DrivingInfoTripContent';
import {ERROR} from '@di-shared/const';
import useUnitConverter from '@di-shared/utils/useUnitConverter';
import useCldrDatetimeFormatter from '@di-shared/utils/useCldrDatetimeFormatter';

export default function ({vin, selectedDate}: DrivingInfoTripProps) {
  return (
    <ErrorBoundary fallback={<></>}>
      <Suspense fallback={<FallbackLoading />}>
        <Inner
          key={getYYYYMMFormat(selectedDate)}
          vin={vin}
          selectedDate={selectedDate}
        />
      </Suspense>
    </ErrorBoundary>
  );
}

function Inner({vin, selectedDate}: DrivingInfoTripProps) {
  const navigate = useNavigate();
  const today = new Date();
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const {verifiedCcsAgreeDate, focusTripDate, tripDays} = useTripDayKr({vin, selectedDate});
  const [innerDate, setInnerDate] = useState(focusTripDate);

  return (
    <div className="section_container">
      <div className="section_container_header">
        <h4 className="title_xxsmall js-tooltip-left-width">{t('T10040')}</h4>
      </div>
      <ReactDatePicker
        inline
        calendarClassName={'show-header'}
        locale={getDateFnsLocale()}
        disabledKeyboardNavigation
        onChange={() => {}}
        renderCustomHeader={() => null}
      />
      <Swiper
        key={getYYYYMMFormat(innerDate)}
        initialSlide={getWeekIndexOfDate(innerDate)}
        style={{overflow: 'hidden'}}
        modules={[Pagination]}
        observeParents
        observer
      >
        {getWeeksOfMonth(innerDate).map((_, index) => {
          return (
            <SwiperSlide key={index}>
              <ReactDatePicker
                inline
                key={getYYYYMMFormat(innerDate)}
                locale={getDateFnsLocale()}
                selected={innerDate}
                minDate={verifiedCcsAgreeDate}
                maxDate={today}
                filterDate={(date) => isSameYearAndMonth(date, innerDate)}
                disabledKeyboardNavigation
                calendarClassName={`show-first-week-${index + 1}`}
                weekDayClassName={() => 'react-datepicker--disabled'}
                onChange={(date: Date) => setInnerDate(date)}
                onMonthChange={(date) => setInnerDate(date)}
                renderCustomHeader={() => null}
                renderDayContents={(dayOfMonth, date: Date) =>
                  !isSameYearAndMonth(date, innerDate) ? null : (
                    <>
                      {dayOfMonth}
                      {isTripOnDay(tripDays, dayOfMonth) && (
                        <span className="has_data">
                          <span className="blind">{t('T10102')}</span>
                        </span>
                      )}
                    </>
                  )
                }
              />
            </SwiperSlide>
          );
        })}
      </Swiper>
      <DrivingInfoTripContent
        vin={vin}
        selectedDate={innerDate}
      />
      <button
        className="btn btn_large btn_filled btn_full"
        type="button"
        onClick={() => {
          emitEventLog({
            event: 'di_driveinfo_interaction',
            data_action: 'view driveinfo',
            data_name: t('T10060_$month', {month: CldrDatetimeFormatter.format(innerDate, 'MMMM')}),
          });

          navigate(ROUTE_PATHS.HISTORY_$DATE.getFullPath(vin, getYYYYMMddFormat(innerDate)));
        }}
      >
        {t('T10060_$month', {month: CldrDatetimeFormatter.format(innerDate, 'MMMM')})}
      </button>
    </div>
  );
}

function FallbackLoading() {
  const today = new Date();
  const {defaultDistanceSymbol} = useUnitConverter();
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();

  return (
    <div className="section_container skeleton">
      <div className="section_container_header">
        <h4 className="title_xxsmall js-tooltip-left-width">{t('T10040')}</h4>
      </div>
      <ReactDatePicker
        inline
        calendarClassName={'show-header'}
        locale={getDateFnsLocale()}
        disabledKeyboardNavigation
        onChange={() => {}}
        renderCustomHeader={() => null}
      />
      <ReactDatePicker
        inline
        calendarClassName={`show-first-week-1`}
        locale={getDateFnsLocale()}
        disabledKeyboardNavigation
        onChange={() => null}
        weekDayClassName={() => 'react-datepicker--disabled'}
        renderCustomHeader={() => null}
      />
      <div className="trip_summary">
        <div className="trip_date skeleton">
          <span className="trip_date_text">{CldrDatetimeFormatter.format(today, 'MMMMEEEEd')}</span>
          <span className="trip_date_today">{t('T10008')}</span>
        </div>
        <div className="trip_summary_list type_error skeleton">
          <div className="trip_summary_item">
            <span className="trip_summary_title">{t('T10052')}</span>
            <div className="trip_summary_info">
              <span className="trip_summary_value">{ERROR.DASH}</span>
              <span className="trip_summary_unit">{defaultDistanceSymbol}</span>
            </div>
          </div>
          <div className="divider divider_vertical" />
          <div className="trip_summary_item">
            <span className="trip_summary_title">{t('T10053')}</span>
            <div className="trip_summary_info">
              <span className="trip_summary_value">{ERROR.DASH}</span>
              <span className="trip_summary_unit">{t('T10054')}</span>
              <span className="trip_summary_value">{ERROR.DASH}</span>
              <span className="trip_summary_unit">{t('T10055')}</span>
            </div>
          </div>
          <div className="divider divider_vertical" />
          <div className="trip_summary_item">
            <span className="trip_summary_title">{t('T10057')}</span>
            <div className="trip_summary_info">
              <span className="trip_summary_value">{ERROR.DASH}</span>
              <span className="trip_summary_unit">{t('T10056')}</span>
            </div>
          </div>
        </div>
      </div>
      <div
        className="trip_info skeleton"
        style={{height: '126px'}}
      >
        <span className="blind">trip info detail</span>
        <div className="empty_area">
          <span className="ic ic_caution_32px" />
          <span
            className="body_small_r"
            style={{whiteSpace: 'pre-wrap'}}
          >
            {t('T10175')}
          </span>
        </div>
      </div>
      <button
        className="btn btn_large btn_filled btn_full"
        type="button"
      >
        {t('T10060_$month', {month: ''})}
      </button>
    </div>
  );
}
